<div class="surface-card p-4 shadow-2 border-round">
  <div class="flex align-items-center justify-content-between mb-3">
    <span class="font-medium text-xl text-900">{{ heading }}</span>
    <button
      pButton
      type="button"
      icon="pi pi-refresh"
      class="p-button-text p-button-sm"
      [loading]="loading"
      (click)="load()"
      title="Reload"
    ></button>
  </div>

  <p-skeleton *ngIf="loading && !view" height="4rem"></p-skeleton>
  <div *ngIf="error" class="text-red-600 text-sm">{{ error }}</div>

  <ng-container *ngIf="view">
    <ng-container *ngIf="showCircuit">
      <div class="text-900 font-medium mb-2">Circuit</div>
      <div *ngIf="!view.circuit" class="text-600 text-sm mb-3">No circuit on file for {{ view.circuit_no }}.</div>
      <div *ngIf="view.circuit as c" class="grid mb-3">
        <div class="col-12 md:col-4">
          <div class="text-500 text-sm">Circuit</div>
          <div class="mt-1 text-900">{{ c.circuit_no }}</div>
        </div>
        <div class="col-12 md:col-4">
          <div class="text-500 text-sm">Speed (down / up)</div>
          <div class="mt-1 text-900">{{ c.download_mbps ?? '-' }} / {{ c.upload_mbps ?? '-' }} Mbps</div>
        </div>
        <div class="col-12 md:col-4">
          <div class="text-500 text-sm">Provisioned</div>
          <div class="mt-1 text-900">{{ c.provisioning_date ? (c.provisioning_date | date: 'yyyy-MM-dd') : '-' }}</div>
        </div>
        <div class="col-12 md:col-4">
          <div class="text-500 text-sm">PPPoE username</div>
          <div class="mt-1 text-900">{{ c.pppoe_username || '-' }}</div>
        </div>
        <div class="col-12 md:col-4" *ngIf="c.pppoe_password">
          <div class="text-500 text-sm">PPPoE password</div>
          <div class="mt-1 flex align-items-center gap-2">
            <span class="text-900">{{ showPppoePassword ? c.pppoe_password : '********' }}</span>
            <button
              pButton
              type="button"
              class="p-button-text p-button-rounded p-button-sm"
              [icon]="showPppoePassword ? 'pi pi-eye-slash' : 'pi pi-eye'"
              (click)="showPppoePassword = !showPppoePassword"
              [attr.aria-label]="showPppoePassword ? 'Hide PPPoE password' : 'Show PPPoE password'"
            ></button>
          </div>
        </div>
        <div class="col-12 md:col-4">
          <div class="text-500 text-sm">Wi-Fi SSID</div>
          <div class="mt-1 text-900">{{ c.wifi_ssid || '-' }}</div>
        </div>
        <div class="col-12 md:col-4" *ngIf="c.wifi_ssid_password">
          <div class="text-500 text-sm">Wi-Fi password</div>
          <div class="mt-1 flex align-items-center gap-2">
            <span class="text-900">{{ showWifiPassword ? c.wifi_ssid_password : '********' }}</span>
            <button
              pButton
              type="button"
              class="p-button-text p-button-rounded p-button-sm"
              [icon]="showWifiPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"
              (click)="showWifiPassword = !showWifiPassword"
              [attr.aria-label]="showWifiPassword ? 'Hide Wi-Fi password' : 'Show Wi-Fi password'"
            ></button>
          </div>
        </div>
        <div class="col-12 md:col-8" *ngIf="c.address">
          <div class="text-500 text-sm">Address</div>
          <div class="mt-1 text-900">{{ c.address }}</div>
        </div>
      </div>
      <div class="text-900 font-medium mb-2">Device</div>
    </ng-container>

    <div *ngIf="!view.device" class="text-600 text-sm mb-3">
      No device is linked to circuit {{ view.circuit_no }}.
    </div>

    <div *ngIf="view.device as device" class="grid mb-2">
      <div class="col-12 md:col-4">
        <div class="text-500 text-sm">Status</div>
        <div class="flex align-items-center gap-2 mt-1">
          <p-tag
            [value]="device.status || 'Unknown'"
            [severity]="statusSeverity(device.status)"
          ></p-tag>
          <span *ngIf="device.status_changed_at" class="text-600 text-sm"
            >since {{ device.status_changed_at | dateAsAgo }}</span
          >
        </div>
      </div>
      <div class="col-12 md:col-4">
        <div class="text-500 text-sm">Signal at OLT</div>
        <div class="mt-1 font-medium" [ngClass]="signalClass(device.olt_rx_dbm)">
          {{ device.olt_rx_dbm !== null && device.olt_rx_dbm !== undefined ? (device.olt_rx_dbm | number: '1.1-2') + ' dBm' : '-' }}
        </div>
      </div>
      <div class="col-12 md:col-4">
        <div class="text-500 text-sm">ONU Rx / Tx</div>
        <div class="mt-1 text-900">
          {{ device.onu_rx_dbm !== null && device.onu_rx_dbm !== undefined ? (device.onu_rx_dbm | number: '1.1-2') : '-' }}
          /
          {{ device.onu_tx_dbm !== null && device.onu_tx_dbm !== undefined ? (device.onu_tx_dbm | number: '1.1-2') : '-' }}
          dBm
        </div>
      </div>
      <div class="col-12 md:col-4">
        <div class="text-500 text-sm">Device</div>
        <div class="mt-1 text-900">{{ device.serial_no }}</div>
      </div>
      <div class="col-12 md:col-4">
        <div class="text-500 text-sm">OLT / board / port</div>
        <div class="mt-1 text-900">
          {{ device.olt || '-' }} / {{ device.board ?? '-' }} / {{ device.port ?? '-' }}
        </div>
      </div>
      <div class="col-12 md:col-4">
        <div class="text-500 text-sm">Admin status</div>
        <div class="mt-1 text-900">{{ device.admin_status || '-' }}</div>
      </div>
    </div>

    <div *ngIf="view.device as device" class="mt-3">
      <div class="flex align-items-center justify-content-between mb-2">
        <span class="text-900 font-medium">MAC addresses</span>
        <button
          pButton
          type="button"
          icon="pi pi-sync"
          class="p-button-text p-button-sm"
          [label]="device.mac_addresses_fetched_at ? 'Read again' : 'Read from OLT'"
          [loading]="macsLoading"
          (click)="refreshMacs()"
        ></button>
      </div>
      <div *ngIf="macsLoading" class="text-600 text-sm">Reading from the OLT, this can take up to a minute...</div>
      <div *ngIf="macsError" class="text-red-600 text-sm">{{ macsError }}</div>
      <ng-container *ngIf="!macsLoading">
        <div *ngIf="device.mac_addresses?.length" class="flex flex-wrap gap-2">
          <p-tag
            *ngFor="let mac of device.mac_addresses"
            [value]="mac"
            severity="secondary"
            styleClass="font-mono"
          ></p-tag>
        </div>
        <div *ngIf="device.mac_addresses_fetched_at && !device.mac_addresses?.length" class="text-600 text-sm">
          No devices were connected behind the ONT at the last read.
        </div>
        <div *ngIf="!device.mac_addresses_fetched_at" class="text-600 text-sm">
          Not read yet.
        </div>
        <div *ngIf="device.mac_addresses_fetched_at" class="text-500 text-xs mt-1">
          Last read {{ device.mac_addresses_fetched_at | dateAsAgo }}
        </div>
      </ng-container>
    </div>

    <div class="text-900 font-medium mt-3 mb-2">
      Open alarms
      <p-tag
        *ngIf="view.open_alarms?.length"
        [value]="'' + view.open_alarms.length"
        severity="danger"
        styleClass="ml-1"
      ></p-tag>
    </div>
    <div *ngIf="!view.open_alarms?.length" class="text-green-600 text-sm mb-3">
      <i class="pi pi-check-circle mr-1"></i>No open alarms on this circuit.
    </div>
    <ul *ngIf="view.open_alarms?.length" class="list-none p-0 m-0 mb-3">
      <li
        *ngFor="let a of view.open_alarms"
        class="flex align-items-center gap-2 py-2 border-bottom-1 surface-border"
      >
        <i
          class="pi"
          [ngClass]="a.severity === 'major' ? 'pi-times-circle text-red-500' : 'pi-exclamation-circle text-orange-500'"
        ></i>
        <span class="flex-1 text-900">{{ a.cause }}</span>
        <p-tag [value]="sourceLabel(a.source)" severity="info" styleClass="text-xs"></p-tag>
        <span class="text-600 text-sm white-space-nowrap">{{ a.raised_at | dateAsAgo }}</span>
      </li>
    </ul>

    <div class="text-900 font-medium mb-2">History (30 days)</div>
    <div *ngIf="!view.history?.length" class="text-600 text-sm">No network events recorded yet.</div>
    <p-table
      *ngIf="view.history?.length"
      [value]="view.history"
      [rows]="10"
      [paginator]="view.history.length > 10"
      styleClass="p-datatable-sm"
    >
      <ng-template pTemplate="header">
        <tr>
          <th>When</th>
          <th>Event</th>
          <th>Source</th>
          <th>Cleared</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-a>
        <tr>
          <td class="white-space-nowrap">{{ a.raised_at | date: 'yyyy-MM-dd HH:mm' }}</td>
          <td>
            <i
              class="pi mr-1"
              [ngClass]="{
                'pi-times-circle text-red-500': a.severity === 'major',
                'pi-exclamation-circle text-orange-500': a.severity === 'minor',
                'pi-info-circle text-blue-500': a.severity === 'info'
              }"
            ></i>
            {{ a.cause }}
          </td>
          <td>{{ sourceLabel(a.source) }}</td>
          <td class="white-space-nowrap">
            <span *ngIf="a.severity === 'info'">-</span>
            <span *ngIf="a.severity !== 'info'">{{
              a.cleared_at ? (a.cleared_at | date: 'yyyy-MM-dd HH:mm') : 'Open'
            }}</span>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </ng-container>
</div>
